import { useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    if (slug && !articles.some((a) => a.slug === slug)) {
      navigate('/', { replace: true });
    }
  }, [slug, navigate]);

  if (!article) {
    return null;
  }

  const category = categories.find((c) => c.slug === article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    url: `https://taimudaojia.cn/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '亚洲精品一区二区',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://taimudaojia.cn' },
      { '@type': 'ListItem', position: 2, name: category?.name, item: `https://taimudaojia.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://taimudaojia.cn/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <main className="pt-20">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category?.name || '', path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    article.category === 'zone1'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}
                >
                  {category?.name}
                </span>
                <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                <span className="text-foreground-400 text-xs">·</span>
                <span className="text-foreground-400 text-xs">{article.readTime} 分钟阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-500">亚洲精品一区二区编辑团队</p>
                </div>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={`${article.title} - 亚洲精品一区二区`}
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-12
                prose-headings:font-heading prose-headings:text-foreground-900
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-semibold prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:font-medium prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-foreground-800
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
                prose-strong:text-foreground-900 prose-strong:font-semibold
                [&_p]:text-sm [&_p]:md:text-base [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-5
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-semibold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-5
                [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="bg-background-100/70 py-14">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          title={`${related.title} - 亚洲精品一区二区`}
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                        <p className="text-foreground-500 text-xs">{related.readTime} 分钟阅读</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
    </>
  );
}